import { Vector2 } from 'three';

import { Point2D, Point3D } from '@paragon/contract-library';

import { LinkEndResultVisualProps } from '../../three/analysis/LinkEndResultVisual';
import convertForceToVector from './ForceToVectorUtility';
import { findPolarAngle } from './GeneralEdge2DUtility';

const findFarthestPolarAngle = (pointA: Point2D, pointB: Point2D) => {
  const point1 = { x: pointA.x, y: pointA.y };
  const point2 = { x: pointB.x, y: pointB.y };
  if (point1.x < 0) point1.x *= -1;
  if (point1.y < 0) point1.y *= -1;
  if (point2.x < 0) point2.x *= -1;
  if (point2.y < 0) point2.y *= -1;
  return findPolarAngle(pointA, pointB);
};

const swapArrayElements = (arr: any[], indexA: number, indexB: number) => {
  const temp = arr[indexA];
  arr[indexA] = arr[indexB];
  arr[indexB] = temp;
};

export const solveForceVectorProof = (forces: LinkEndResultVisualProps[], startPosition: Point3D) => {
  if (forces.length === 0) return [];
  // convert forces to vectors
  const vectors: ({ x: number; y: number; z: number; index: number; reversed: boolean } | undefined)[] = [];
  for (let i = 0; i < forces.length; i += 1) {
    const vector = convertForceToVector(forces[i]);
    vectors.push({
      x: vector.x,
      y: vector.y,
      z: vector.z,
      index: i,
      reversed: forces[i].axialForce < 0,
    });
  }
  // get longest vector to start with
  let longestVector = new Vector2(0, 0);
  let longestVectorIndex = -1;

  for (let i = 0; i < vectors.length; i += 1) {
    const vector = vectors[i];
    if (!vector) continue;
    const valueComparer = new Vector2(vector.x, vector.y);
    if (longestVector.length() < valueComparer.length()) {
      longestVector = valueComparer;
      longestVectorIndex = i;
    }
  }

  const sortedVectors: { x: number; y: number; z: number; index: number; reversed: boolean }[] = [];
  swapArrayElements(vectors, longestVectorIndex, 0);
  const firstVector = vectors[0];
  if (firstVector) sortedVectors.push(firstVector);

  // sort by largest differance in polar angle
  for (let i = 0; i < vectors.length; i += 1) {
    const vectorI = vectors[i];
    if (vectorI) {
      let lowestPolarAngle = -Infinity;
      let lowestPolarForce = vectorI;
      let lowestPolarIndex = 0;
      let foundMatch = false;
      for (let j = 0; j < vectors.length; j += 1) {
        const vectorJ = vectors[j];
        if (vectorJ && i !== j) {
          const angle = findFarthestPolarAngle(vectorI, vectorJ);
          if (angle > lowestPolarAngle) {
            foundMatch = true;
            lowestPolarAngle = angle;
            lowestPolarForce = vectorJ;
            lowestPolarIndex = j;
          }
        }
      }
      if (foundMatch) {
        sortedVectors.push(lowestPolarForce);
      } else {
        break;
      }
      vectors[i] = undefined;
      // reset loop
      i = -1;
      swapArrayElements(vectors, lowestPolarIndex, 0);
    }
  }
  // get cumalitive position
  const result = [];
  let prevPosition = { x: 0, y: 0, z: 0 };
  for (let i = 0; i < sortedVectors.length; i += 1) {
    const position = {
      x: prevPosition.x + startPosition.x,
      y: prevPosition.y + startPosition.y,
      z: prevPosition.z + startPosition.z,
    };
    if (sortedVectors[i].reversed) {
      position.x += sortedVectors[i].x;
      position.y += sortedVectors[i].y;
      position.z += sortedVectors[i].z;
      result.push({ position, index: sortedVectors[i].index });
    } else {
      result.push({ position, index: sortedVectors[i].index });
    }
    prevPosition = {
      x: prevPosition.x + sortedVectors[i].x,
      y: prevPosition.y + sortedVectors[i].y,
      z: prevPosition.z + sortedVectors[i].z,
    };
  }
  return result;
};
